import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  BRAND_GREEN,
  BRAND_GREEN_DEEP,
  easeOutExpo,
  HERO_IMAGE,
  LOGO_FIXED,
} from "./shared";

/* Hero image with parallax + slow Ken-Burns drift. */
const HeroImage = ({ scrollYProgress }) => {
  const yOffset = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <div className="absolute inset-0" aria-hidden="true">
      <motion.div
        style={{ y: yOffset, scale }}
        className="absolute inset-0"
        data-testid="hero-image"
      >
        <img
          src={HERO_IMAGE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover hero-img-anim"
          style={{ objectPosition: "center 62%" }}
          loading="eager"
          fetchPriority="high"
        />
      </motion.div>

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(108deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.68) 24%, rgba(0,0,0,0.42) 48%, rgba(0,0,0,0.18) 70%, rgba(0,0,0,0) 92%)",
        }}
      />
      <div
        className="absolute inset-x-0 top-0 h-40 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 100%)",
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-2/5 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 100%)",
        }}
      />
    </div>
  );
};

export const HeroSection = React.forwardRef(function HeroSection(_, ref) {
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative w-full h-screen min-h-[600px] overflow-hidden snap-start"
      data-testid="hero-section"
    >
      <HeroImage scrollYProgress={scrollYProgress} />

      <motion.div
        className="absolute inset-0 z-10 flex flex-col"
        style={{ opacity: contentOpacity, y: contentY }}
      >
        {/* Top zone — desktop inline logo.
            We render the PNG inside a tightly-cropped wrapper that contains
            only the *visible* glyph (the source is 1500×1000 with 33.8% top
            and bottom + 23.53% left/right transparent padding). The wrapper
            scales proportionally at any zoom level, so the visible logo can
            never bleed into the H1 below — no fragile negative margins. */}
        <div className="w-full px-6 md:px-12 lg:px-16 pt-10 md:pt-14 lg:pt-16">
          <div className="max-w-3xl">
            <motion.div
              className="hidden md:block"
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.15, ease: easeOutExpo }}
              data-testid="hero-inline-logo"
              style={{
                // Visible logo dims. The source PNG has 33.8% transparent
                // padding top+bottom and 23.53% left+right, but the "M" of
                // MatiMitra extends slightly LEFT of the analyzed bounding
                // box. We expose an extra 4% on the left for safety, so the
                // container aspect ratio = (794+60) : 324 ≈ 2.636 : 1.
                height: "clamp(120px, 17vh, 200px)",
                width: "calc(clamp(120px, 17vh, 200px) * 2.636)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <img
                src={LOGO_FIXED}
                alt="MatiMitra Biochar"
                draggable="false"
                style={{
                  // Image height = container_H / 0.324 (so visible 32.4%
                  // exactly fills container_H). top:-104.3% shifts the
                  // visible glyph's top into the container's y=0.
                  // left:-34.3% shifts the visible glyph's left into the
                  // container's x=0 (minus the 4% safety margin). All
                  // percentages — fully zoom-stable.
                  position: "absolute",
                  height: "calc(100% / 0.324)",
                  width: "auto",
                  maxWidth: "none",
                  top: "-104.3%",
                  left: "-34.3%",
                  filter:
                    "drop-shadow(0 2px 16px rgba(0,0,0,0.55)) drop-shadow(0 1px 3px rgba(0,0,0,0.45))",
                }}
              />
            </motion.div>
          </div>
        </div>

        {/* Flexible spacer keeps the logo near the top and the headline
            cluster near the bottom — independent of stack height, so zoom
            never causes overlap. */}
        <div className="flex-1" aria-hidden="true" />

        {/* Bottom zone — headline + subhead + supporting + CTA */}
        <div className="w-full px-6 md:px-12 lg:px-16 pb-16 md:pb-20 lg:pb-24">
          <div className="max-w-3xl">
            <motion.h1
              className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.02] tracking-[-0.02em] font-light"
              style={{ textShadow: "0 2px 24px rgba(0,0,0,0.55)" }}
              data-testid="hero-h1"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.3, delay: 0.35, ease: easeOutExpo }}
            >
              Durable
              <br />
              Carbon Removal
            </motion.h1>

            <motion.div
              className="mt-8 md:mt-10 font-serif text-white text-xl md:text-2xl leading-[1.5] font-light"
              style={{ textShadow: "0 1px 18px rgba(0,0,0,0.55)" }}
              data-testid="hero-subhead"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.75, ease: easeOutExpo }}
            >
              <div>Built in Odisha.</div>
              <div>Scaling across Bharat.</div>
              <div>Responsibly.</div>
            </motion.div>

            <motion.p
              className="mt-8 md:mt-10 text-white/80 text-sm md:text-base max-w-lg leading-relaxed font-sans font-light"
              style={{ textShadow: "0 1px 14px rgba(0,0,0,0.55)" }}
              data-testid="hero-supporting"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.0, delay: 1.05, ease: easeOutExpo }}
            >
              Crop residue, transformed into biochar — for healthier soil
              and rural prosperity.
            </motion.p>

            <motion.a
              href="#contact"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-2 mt-10 md:mt-12 px-7 py-3.5 text-sm tracking-wide font-sans font-semibold text-white transition-all duration-500 hover:translate-x-1"
              style={{ backgroundColor: BRAND_GREEN }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = BRAND_GREEN_DEEP)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = BRAND_GREEN)
              }
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.0, delay: 1.3, ease: easeOutExpo }}
            >
              Get in touch
              <span
                aria-hidden="true"
                className="text-base leading-none transition-transform duration-500 group-hover:translate-x-1"
              >
                →
              </span>
            </motion.a>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="absolute bottom-6 right-6 md:right-12 z-10 text-white/55 text-[10px] tracking-[0.3em] uppercase font-sans hidden md:flex items-center gap-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5, delay: 1.7 }}
      >
        <span>Scroll</span>
        <span className="block w-12 h-px bg-white/40" />
      </motion.div>
    </section>
  );
});
